import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, DeleteIcon, ArrowLeftIcon, ArrowRightIcon, PencilIcon, CreateInvoiceIcon } from "../assets/dashboard-icon.svg";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCustomers } from "../hooks/useCustomers";
import { Customer } from "../api/customer";
import { useAppDispatch } from "../hooks/useRedux";
import { deleteCustomer, fetchInvoiceEditData } from "../features/customer/customer.slice";
import { notify } from '../helper/notify';
import { useNavigate } from "react-router-dom";
import AddCustomerModal from "../components/add-customer-modal/components/AddCustomerModal";
import EditCustomerModal from "../components/add-customer-modal/components/EditCustomerModal";
import { DashboardButton } from "./dashboard/dashboard-button.component";
import { InvoiceTableSkeleton } from './dashboard/manage-invoice-skeleton.component';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { useSelector } from "react-redux";
import { Skeleton } from '@mui/material';

/* Renders `text` inside a <span> and attaches the platform's `data-tip`
   tooltip (the same dark-bubble-with-arrow styling used by the action-
   button tooltips elsewhere on this page) ONLY when the text overflows
   its container (i.e. is showing the ellipsis). Uses
   `scrollWidth > clientWidth` as the overflow check and re-evaluates
   whenever the element resizes (column width changes, sidebar collapse,
   etc.). When the text fits in full there is no tooltip. */
const TruncatedText: React.FC<{
    text: string;
    className?: string;
}> = ({ text, className }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const check = () => {
            // +1 tolerance for sub-pixel rendering on fractional widths
            setOverflows(el.scrollWidth > el.clientWidth + 1);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [text]);

    /* Wrapped in `.mi-tt` because the inner span has `overflow: hidden`
       (so the ellipsis works) — and `overflow: hidden` would clip a
       tooltip pseudo-element on that same span. The wrapper has no
       overflow constraint, so its `::after` tooltip can render freely
       above the row. The wrapper carries the `data-tip` attribute that
       the SCSS picks up. */
    return (
        <span className="mi-tt" data-tip={overflows ? text : undefined}>
            <span ref={ref} className={className}>
                {text}
            </span>
        </span>
    );
};

export const MyClientPageSection = ({
    className,
}: {
    className?: string
}
) => {
        const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const dispatch = useAppDispatch();
    const [deletingCustomerId, setDeletingCustomerId] = useState<number | null>(null);
    const lang = new URLSearchParams(window.location.search).get('lang') || 'en-us';
    const customerSearchParam = new URLSearchParams(window.location.search).get('customerSearch');
    const searchParam = new URLSearchParams(window.location.search).get('search');
    const customerSearchVal = customerSearchParam ? decodeURIComponent(customerSearchParam) : '';
    const searchVal = searchParam ? decodeURIComponent(searchParam) : '';
    const combinedSearchVal = [customerSearchVal, searchVal].filter(Boolean).join(' + ');
    const navigate = useNavigate();
    const {
        loading,
        error,
        searchQuery,
        currentPage,
        totalPages,
        pageSize,
        totalCount,
        paginatedCustomers,
        setSearchQuery,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        refetch,
    } = useCustomers(10, combinedSearchVal);

    const getClientInitial = (name?: string) => {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
    };

    const getAvatarVariant = (name?: string) => {
        const key = (name || '').trim();
        let sum = 0;
        for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
        return (sum % 5) + 1;
    };
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const userID = useSelector((state: any) => state?.auth?.user?.userId);

    // Skeleton gating: full-page skeleton only on the very first fetch and on
    // explicit pagination changes. Typing in the search bar must NOT replace
    // the toolbar — only the body rows shimmer in that case.
    const [initialLoad, setInitialLoad] = useState(true);
    const [isPaging, setIsPaging] = useState(false);
    useEffect(() => {
        if (!loading) {
            setInitialLoad(false);
            setIsPaging(false);
        }
    }, [loading]);
    const isBodyLoading = loading && !initialLoad && !isPaging;
    const hasActiveQuery = (searchQuery?.trim().length ?? 0) > 0;

    // Wrap pagination handlers so they trigger the full skeleton, while a
    // search-triggered re-fetch only shows the body skeleton.
    const handleGoToPage = (p: number) => { setIsPaging(true); setCurrentPage(p); };
    const handleGoNext   = () => { setIsPaging(true); goToNextPage(); };
    const handleGoPrev   = () => { setIsPaging(true); goToPreviousPage(); };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleDeleteCustomer = async (customerId: number) => {
        try {
            setDeletingCustomerId(customerId);
            await dispatch(deleteCustomer(customerId)).unwrap();
            /* Context-specific success key — `DELETE_SUCCESS` is
               used by the drafts flow and reads "Draft deleted",
               which is misleading here after a customer delete. */
            notify.success(String(t('CUSTOMER_DELETED_SUCCESS', { defaultValue: 'Customer deleted' })));
            await refetch();
        } catch (error: any) {
            const msg = typeof error === 'string' ? error : (error?.message || t('DELETE_FAILED'));
            notify.error(String(msg));
        } finally {
            setDeletingCustomerId(null);
        }
    };
    const openDeleteModal = (id: number) => {
    setSelectedInvoiceId(id);
    setShowDeleteModal(true);
};

const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedInvoiceId(null);
};

const confirmDelete = async () => {
    if (!selectedInvoiceId) return;

    try {
        setDeleteLoading(true);
        await handleDeleteCustomer(selectedInvoiceId);
    } finally {
        setDeleteLoading(false);
        closeDeleteModal();
    }
};

    const handleEditCustomer = (customer: Customer) => {
        setSelectedCustomer(customer);
        setShowEditModal(true);
    };
          useEffect(() => {
        if (userID) {
          dispatch(fetchInvoiceEditData(userID));
        }
      }, [dispatch, userID]);

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    if (initialLoad || isPaging) {
        return (
            <div className={`invoice-table-container ${className}`}>
                <InvoiceTableSkeleton columns={6} filters="search" />
            </div>
        );
    }


    return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">
                    {/* Use the dedicated "Clients" key (same one the page hero
                        uses). The base MY_CLIENTS key still resolves to
                        "Customers" everywhere else (sidebar nav etc). */}
                    {t('MY_CLIENTS_HERO_TITLE', { defaultValue: 'My Clients' })}
                    <span className="mi-count-badge">{totalCount ?? paginatedCustomers.length}</span>
                </h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('SEARCH_CLIENT', { defaultValue: 'Search client' })}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>
                    <DashboardButton
                        variant="primary"
                        // icon={<PlusIcon />}
                        onClick={() => setShowCustomerModal(true)}
                    >
                        {t('ADD_CLIENT')}
                    </DashboardButton>
                </div>
            </div>

            <div className="invoice-table-content">
                {/* Table — kept mounted while a search-triggered re-fetch is in
                    flight so the search bar above keeps focus and the only thing
                    that shimmers is the body. */}
                {!error && paginatedCustomers.length >= 0 && (
                    <div className="invoice-table-scroll-wrapper">
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('ID') || 'Id'}</TableColumn>
                                <TableColumn>{t('CLIENT', { defaultValue: 'Client' })}</TableColumn>
                                <TableColumn>{t('ORG_NO') || 'Org No'}</TableColumn>
                                <TableColumn>{t('REFERENCE') || 'Reference'}</TableColumn>
                                <TableColumn>{t('ADDRESS') || 'Address'}</TableColumn>
                                <TableColumn>{t('ACTIONS', { defaultValue: 'Actions' })}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {/* Empty state — animated, with two variants:
                                  - hasActiveQuery: "no matching clients" (search icon)
                                  - otherwise: "no clients yet" (people icon)
                                Hides during a search-triggered body load so the
                                table doesn't flash "no clients" between keystrokes.
                                Styling comes from .mi-empty-state in
                                manage-invoice.style.scss (floating dots + halo + gradient title). */}
                            {!isBodyLoading && paginatedCustomers.length === 0 && (
                                <div className="mi-empty-state">
                                    <div className="mi-empty-state__icon" aria-hidden="true">
                                        {hasActiveQuery ? (
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M9 11h4M11 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
                                            </svg>
                                        ) : (
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
                                                <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" opacity="0.55" />
                                                <path d="M15 20c0-2.4 1.5-4.5 3.7-5.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
                                            </svg>
                                        )}
                                    </div>
                                    <h4 className="mi-empty-state__title">
                                        {hasActiveQuery
                                            ? t('NO_MATCHING_CUSTOMERS', { defaultValue: 'No matching customers' })
                                            : t('NO_CUSTOMERS', { defaultValue: 'No customers yet' })}
                                    </h4>
                                    <p className="mi-empty-state__message">
                                        {hasActiveQuery
                                            ? t('NO_MATCHING_CUSTOMERS_HINT', { defaultValue: 'Try a different search term or clear your filters.' })
                                            : t('NO_CUSTOMERS_HINT', { defaultValue: 'Add your first customer to start sending invoices.' })}
                                    </p>
                                    {error && <p className="mi-empty-state__message">{error}</p>}
                                </div>
                            )}
                            {isBodyLoading ? (
                                // Body-only skeleton during a search-triggered re-fetch.
                                // Row count matches the visible row count (or 4) so the
                                // table doesn't jump in height while the user types.
                                Array.from({ length: Math.max(paginatedCustomers.length, 4) }).map((_, r) => (
                                    <TableRow key={`s-${r}`}>
                                        {Array.from({ length: 6 }).map((_, c) => (
                                            <TableCell key={c}>
                                                {c === 1 ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                                                        <Skeleton variant="circular" width={34} height={34} animation="wave" />
                                                        <div style={{ flex: 1 }}>
                                                            <Skeleton variant="text" width="70%" height={14} animation="wave" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <Skeleton variant="text" width="72%" height={16} animation="wave" />
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : isMobile ? (
                                paginatedCustomers?.map((customer: Customer) => (
                                    <MobileCardWrapper key={customer.customerId}>
                                        <MobileCardRow label={t('ID') || 'Customer Name'}>
                                            <span className="mi-invoice__number">{customer.customerId}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME') || 'Email'}>
                                            <span className="mi-client">
                                                <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(customer.name)}`}>{getClientInitial(customer.name)}</span>
                                                <span className="mi-client__info">
                                                    {/* Native tooltip only when the text is actually clipped — TruncatedText
                                                        watches its own size and toggles `title` accordingly. */}
                                                    <TruncatedText className="mi-client__name" text={customer.name || 'N/A'} />
                                                    {customer.email && (
                                                        <TruncatedText className="mi-client__email" text={customer.email} />
                                                    )}
                                                </span>
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ORG_NO') || 'Phone'}>
                                            {customer.orgNo || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('REFERENCE') || 'Address'}>
                                            {customer.reference || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ADDRESS') || 'City'}>
                                            {customer.address || 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ACTION_LABEL') || 'Action'}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("EDIT_CUSTOMER_MODAL_TITLE")}
                                                    onClick={() => handleEditCustomer(customer)}
                                                >
                                                    <PencilIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("CREATE_INVOICE")}
                                                    onClick={() => navigate(`/v2/create-invoice?lang=${lang}&customerId=${customer.customerId}`)} // Added onClick handler
                                                >
                                                    <CreateInvoiceIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("DELETE")}
                                                    
                                                    onClick={() => openDeleteModal(customer.customerId!)}
                                                    disabled={deletingCustomerId === customer.customerId}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                paginatedCustomers.map((customer: Customer) => (
                                    <TableRow key={customer.customerId}>
                                        <TableCell>
                                            <span className="mi-invoice__number">{customer.customerId}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="mi-client">
                                                <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(customer.name)}`}>{getClientInitial(customer.name)}</span>
                                                <span className="mi-client__info">
                                                    {/* Native tooltip only when the text is actually clipped — TruncatedText
                                                        watches its own size and toggles `title` accordingly. */}
                                                    <TruncatedText className="mi-client__name" text={customer.name || 'N/A'} />
                                                    {customer.email && (
                                                        <TruncatedText className="mi-client__email" text={customer.email} />
                                                    )}
                                                </span>
                                            </span>
                                        </TableCell>
                                        <TableCell>{customer.orgNo || 'N/A'}</TableCell>
                                        <TableCell>{customer.reference || 'N/A'}</TableCell>
                                        <TableCell>{customer.address || 'N/A'}</TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("EDIT_CUSTOMER_MODAL_TITLE")}
                                                    onClick={() => handleEditCustomer(customer)}
                                                >
                                                    <PencilIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("CREATE_INVOICE")}
                                                    onClick={() => navigate(`/v2/create-invoice?lang=${lang}&customerId=${customer.customerId}`)} // Added onClick handler
                                                >
                                                    <CreateInvoiceIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("DELETE")}
                                                    onClick={() => openDeleteModal(customer.customerId!)}
                                                    disabled={deletingCustomerId === customer.customerId}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                    </div>
                )}


                {/* Footer: count + pagination */}
                {paginatedCustomers.length > 0 && (
                    <div className="invoice-table-footer">
                        <span className="invoice-table-footer__count">
                            {(() => {
                                const total = totalCount ?? 0;
                                const size = pageSize ?? 10;
                                const start = total === 0 ? 0 : (currentPage - 1) * size + 1;
                                const end = Math.min(currentPage * size, total);
                                return t('SHOWING_CLIENTS_COUNT', {
                                    start,
                                    end,
                                    total,
                                    defaultValue: 'Showing {{start}}–{{end}} of {{total}} clients',
                                });
                            })()}
                        </span>
                        {totalPages > 1 && (
                        <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={handleGoPrev}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => handleGoToPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={handleGoNext}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                        </div>
                        )}
                    </div>
                )}
            </div>
            <AddCustomerModal
                show={showCustomerModal}
                onHide={() => setShowCustomerModal(false)}
                onSave={() => {
                    setShowCustomerModal(false);
                    refetch();
                }}
            />
            <EditCustomerModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedCustomer(null);
                }}
                onSave={() => {
                    setShowEditModal(false);
                    setSelectedCustomer(null);
                    refetch();
                }}
                selectedCustomer={selectedCustomer?.customerId}
            />
             <ConfirmDeleteModal
    open={showDeleteModal}
    title={t('DELETE_CUSTOMER')}
    description={t('DELETE_CUSTOMER_CONFIRM')}
    loading={deleteLoading}
    onCancel={closeDeleteModal}
    onConfirm={confirmDelete}
/>
        </div>
    );
}